import { DisplayMagnitude } from '@snap-types/models/quoting-config/enums';
import { Decimal } from '@snap-types/types/decimal';
import { z } from 'zod/v4';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { getQuoteLineOrThrow } from '../utilities/action-guards';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

type ValidateQuantityParams = {
  quantity: string | Decimal;
  quoteLineUuid: string;
  quotePriceUuid: string;
};

export function validateQuantity(
  state: WritableQuoteBuilderStateType,
  { quantity, quoteLineUuid, quotePriceUuid }: ValidateQuantityParams,
) {
  const { lastWin, priceStrategy } = getQuoteLineOrThrow(state, quoteLineUuid);
  const quantityString = quantity.toString();

  const invalidIssueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    type: 'invalid',
    field: 'quantity',
  });

  let validator = z.coerce
    .number({
      error: 'Quantity is required',
    })
    .positive({ message: 'Quantity must be greater than 0' });

  if (state.displayQuantity === DisplayMagnitude.ONES) {
    validator = validator.int({ message: 'Quantity must be an integer' });
  }

  if (
    state.quantityRange &&
    priceStrategy === 'PriceSegmentation' &&
    lastWin.quantity
  ) {
    const minQuantity = lastWin.quantity
      .minus(lastWin.quantity.times(state.quantityRange))
      .floor()
      .toNumber();
    const maxQuantity = lastWin.quantity
      .plus(lastWin.quantity.times(state.quantityRange))
      .ceil()
      .toNumber();

    validator = validator.gte(minQuantity, {
      message: `Quantity must be greater than or equal to ${minQuantity}`,
    });
    validator = validator.lte(maxQuantity, {
      message: `Quantity must be less than or equal to ${maxQuantity}`,
    });
  }

  const result = validator.safeParse(quantityString);

  if (!result.success) {
    const error = z.flattenError(result.error).formErrors[0];

    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid quantity',
      description: error,
      severity: 'error',
      type: 'invalid',
      field: 'quantity',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'quantity',
        }),
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  return new Decimal(result.data);
}
